package at.theatervogelweide.admin.web.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.Account
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ChangePasswordRequest
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.PasswordLinkInfo
import at.theatervogelweide.admin.contract.PasswordRules
import at.theatervogelweide.admin.contract.SetPasswordRequest
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import kotlinx.browser.window
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

private val signIn = Bem("admin-sign-in")
private val alert = Bem("admin-alert")
private val stack = Bem("admin-stack")
private val fieldset = Bem("admin-fieldset")

private const val REPEAT = "repeat"

/** The new password twice; the second must match, which only the web app checks. */
@Composable
private fun NewPasswordFields(
    idPrefix: String,
    field: String,
    password: String,
    repeat: String,
    fieldErrors: List<FieldError>,
    onPasswordChange: (String) -> Unit,
    onRepeatChange: (String) -> Unit,
) {
    AdminTextField(
        id = "$idPrefix-$field",
        label = "Neues Passwort",
        value = password,
        onValueChange = onPasswordChange,
        type = InputType.Password,
        error = fieldErrors.firstOrNull { it.field == field }?.message,
        hint = PasswordRules.DESCRIPTION,
        autocomplete = "new-password",
    )
    AdminTextField(
        id = "$idPrefix-$REPEAT",
        label = "Neues Passwort wiederholen",
        value = repeat,
        onValueChange = onRepeatChange,
        type = InputType.Password,
        error = fieldErrors.firstOrNull { it.field == REPEAT }?.message,
        autocomplete = "new-password",
    )
}

private fun repeatError(password: String, repeat: String): List<FieldError> =
    if (password != repeat) listOf(FieldError(REPEAT, "Die beiden Passwörter sind verschieden.")) else emptyList()

/**
 * The page of a one-time link (#66), without the app shell and without a session, like sign-in. The token comes from
 * the fragment, which the page drops from the address at once, so it stays out of the history. [onSignedIn] gets the
 * account the link signed in, or `null` when it can't use the admin app; [onSignIn] goes to sign-in.
 */
@Composable
fun SetPasswordScreen(api: AdminApi, onSignedIn: (Account?) -> Unit, onSignIn: () -> Unit) {
    val scope = rememberCoroutineScope()
    val token = remember {
        window.location.hash.removePrefix("#").also {
            window.history.replaceState(window.history.state, "", window.location.pathname)
        }
    }
    var info by remember { mutableStateOf<PasswordLinkInfo?>(null) }
    var linkError by remember { mutableStateOf<String?>(null) }
    // a failed lookup that isn't the link's fault can be tried again; the token is only in this state now
    var lookupFailed by remember { mutableStateOf(false) }
    var lookupAttempt by remember { mutableStateOf(0) }
    var password by remember { mutableStateOf("") }
    var repeat by remember { mutableStateOf("") }
    var fieldErrors by remember { mutableStateOf(emptyList<FieldError>()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }

    // reloading or leaving the page while the password is on its way asks first
    DisposableEffect(submitting) {
        val warn: (Event) -> Unit = { event -> event.preventDefault() }
        if (submitting) {
            window.addEventListener("beforeunload", warn)
        }
        onDispose { window.removeEventListener("beforeunload", warn) }
    }

    LaunchedEffect(token, lookupAttempt) {
        if (token.isEmpty()) {
            linkError = "Der Link ist unvollständig. Bitte öffne ihn noch einmal so, wie du ihn bekommen hast."
            return@LaunchedEffect
        }
        when (val result = runCatching { api.checkPasswordLink(token) }.getOrElse { AdminResult.Failure(AdminError.internal()) }) {
            is AdminResult.Success -> info = result.value
            is AdminResult.Failure -> {
                linkError = result.error.message
                lookupFailed = result.error.code != ErrorCode.NOT_FOUND
            }
        }
    }

    fun submit() {
        val request = SetPasswordRequest(token, password)
        fieldErrors = request.validate() + repeatError(password, repeat)
        formError = null
        if (fieldErrors.isNotEmpty() || submitting) {
            return
        }

        submitting = true
        // not cancelled when the page changes: the server may already have used the link, and the answer brings the session
        scope.launch(NonCancellable) {
            val result = runCatching { api.setPasswordWithLink(request) }.getOrElse { AdminResult.Failure(AdminError.internal()) }
            submitting = false
            when (result) {
                is AdminResult.Success -> onSignedIn(result.value)
                // used, expired or revoked meanwhile: the form can't help any more, the error card leads to sign-in
                is AdminResult.Failure -> if (result.error.code == ErrorCode.NOT_FOUND) {
                    linkError = result.error.message
                    lookupFailed = false
                } else {
                    fieldErrors = result.error.fieldErrors
                    formError = result.error.message.takeIf { result.error.fieldErrors.isEmpty() }
                }
            }
        }
    }

    Div({ classes(signIn.block) }) {
        Div({ classes(signIn.element("panel").className) }) {
            Div({ classes(signIn.element("brand").className) }) {
                Span({
                    classes(signIn.element("mark").className)
                    attr("aria-hidden", "true")
                }) { Text("t") }
                Span({ classes(signIn.element("brand-text").className) }) {
                    Span({ classes(signIn.element("brand-name").className) }) { Text("Theater Vogelweide") }
                    Span({ classes(signIn.element("brand-area").className) }) { Text("Verwaltung") }
                }
            }

            val current = info
            when {
                linkError != null -> AdminCard(
                    title = "Passwort festlegen",
                    id = "passwort-festlegen",
                    titleLevel = 1,
                    footer = {
                        if (lookupFailed) {
                            AdminButton(variant = ButtonVariant.PRIMARY, onClick = {
                                linkError = null
                                lookupFailed = false
                                lookupAttempt++
                            }) { Text("Erneut versuchen") }
                        }
                        AdminButton(variant = if (lookupFailed) ButtonVariant.SECONDARY else ButtonVariant.PRIMARY, block = !lookupFailed, onClick = onSignIn) {
                            Text("Zur Anmeldung")
                        }
                    },
                ) {
                    P({
                        classes(alert.block)
                        attr("role", "alert")
                    }) { Text(linkError!!) }
                }

                current == null -> P { Text("Lade …") }

                else -> Form(attrs = {
                    attr("novalidate", "")
                    onSubmit { event ->
                        event.preventDefault()
                        submit()
                    }
                }) {
                    AdminCard(
                        title = if (current.newAccount) "Willkommen in der Verwaltung" else "Neues Passwort festlegen",
                        id = "passwort-festlegen",
                        titleLevel = 1,
                        footer = {
                            AdminButton(variant = ButtonVariant.PRIMARY, type = ButtonType.Submit, disabled = submitting, block = true) {
                                Text(if (submitting) "Speichert …" else "Passwort festlegen")
                            }
                        },
                    ) {
                        P { Text("Für das Konto ${current.email}. Der Link gilt nur einmal.") }
                        // what is sent can't change while it is on its way
                        Fieldset({
                            classes(fieldset.block)
                            if (submitting) {
                                attr("disabled", "")
                            }
                        }) {
                            NewPasswordFields(
                                idPrefix = "link",
                                field = SetPasswordRequest.PASSWORD,
                                password = password,
                                repeat = repeat,
                                fieldErrors = fieldErrors,
                                onPasswordChange = { password = it },
                                onRepeatChange = { repeat = it },
                            )
                        }
                        formError?.let {
                            P({
                                classes(alert.block)
                                attr("role", "alert")
                            }) { Text(it) }
                        }
                    }
                }
            }
        }
    }
}

/**
 * "Passwort ändern" for the signed-in account (#66). Every other session of the account ends with it. Typed passwords
 * and a change on its way count as unsaved, so [guard] asks before leaving: the answer brings this session's new cookie.
 */
@Composable
fun ChangePasswordScreen(api: AdminApi, guard: UnsavedChangesGuard, onAccountChanged: (Account) -> Unit) {
    val scope = rememberCoroutineScope()
    var current by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var repeat by remember { mutableStateOf("") }
    var fieldErrors by remember { mutableStateOf(emptyList<FieldError>()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }
    var changed by remember { mutableStateOf(false) }
    ReportUnsavedChanges(guard, submitting || current.isNotEmpty() || password.isNotEmpty() || repeat.isNotEmpty())

    fun submit() {
        val request = ChangePasswordRequest(current, password)
        fieldErrors = request.validate() + repeatError(password, repeat)
        formError = null
        changed = false
        if (fieldErrors.isNotEmpty() || submitting) {
            return
        }

        submitting = true
        scope.launch {
            val result = runCatching { api.changePassword(request) }.getOrElse { AdminResult.Failure(AdminError.internal()) }
            submitting = false
            when (result) {
                is AdminResult.Success -> {
                    // as the renewed session has it, e.g. with an e-mail address or role another superuser changed
                    onAccountChanged(result.value)
                    current = ""
                    password = ""
                    repeat = ""
                    changed = true
                }

                is AdminResult.Failure -> {
                    fieldErrors = result.error.fieldErrors
                    formError = result.error.message.takeIf { result.error.fieldErrors.isEmpty() }
                }
            }
        }
    }

    AdminPageHeader("Passwort ändern")

    AdminPageBody {
        Div({ classes(stack.block, stack.modifier("form")) }) {
            Form(attrs = {
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    submit()
                }
            }) {
                AdminCard(
                    title = "Dein Passwort",
                    id = "passwort",
                    footer = {
                        AdminButton(variant = ButtonVariant.PRIMARY, type = ButtonType.Submit, disabled = submitting) {
                            Text(if (submitting) "Speichert …" else "Passwort ändern")
                        }
                    },
                ) {
                    if (changed) {
                        AdminCallout(alert = true) {
                            Text("Dein Passwort ist geändert. Deine anderen Anmeldungen und verbundenen Anwendungen sind abgemeldet.")
                        }
                    }
                    Fieldset({
                        classes(fieldset.block)
                        if (submitting) {
                            attr("disabled", "")
                        }
                    }) {
                        AdminTextField(
                            id = "passwort-${ChangePasswordRequest.CURRENT_PASSWORD}",
                            label = "Aktuelles Passwort",
                            value = current,
                            onValueChange = { current = it },
                            type = InputType.Password,
                            error = fieldErrors.firstOrNull { it.field == ChangePasswordRequest.CURRENT_PASSWORD }?.message,
                            autocomplete = "current-password",
                        )
                        NewPasswordFields(
                            idPrefix = "passwort",
                            field = ChangePasswordRequest.NEW_PASSWORD,
                            password = password,
                            repeat = repeat,
                            fieldErrors = fieldErrors,
                            onPasswordChange = { password = it },
                            onRepeatChange = { repeat = it },
                        )
                    }
                    formError?.let {
                        P({
                            classes(alert.block)
                            attr("role", "alert")
                        }) { Text(it) }
                    }
                    AdminCallout {
                        Text("Danach bist du überall sonst abgemeldet, und verbundene Anwendungen wie Claude müssen neu verbunden werden.")
                    }
                }
            }
        }
    }
}
